// The game's red tally counter (src/objects/ui/counter_input.gd) on the phone:
// the same body and side nubs, three rolling digit drums in virtue_large_bold,
// and the same feel. A nub slides into the body when pressed, sinks deeper
// once a hold starts repeating, and pops out past rest on release. A hold
// repeats on the game's ramp. Bumping 0 or 999 shakes the body. Every step
// ticks, as the game's does.
//
// The keyboard feeds it too (`type`, `backspace`, `clear`):
// a typed digit rolls into place on the drums like a stepped one.

import { buzz, prefersLessMotion } from './dom';
import { type Font, loadFont } from './pixelText';
import { play } from './sfx';
import { ART_SCALE, loadSprite, spriteCanvas } from './sprites';

export const COUNTER_MAX = 999;
const DIGITS = 3;

// The game's layout, in sprite pixels, cut down to the body and its nubs.
// MARGIN leaves room for a nub's overshoot and the bump shake.
const MARGIN = 4;
const BUTTON_W = 32;
const BODY_SIZE = 128;
const BODY_X = MARGIN + 6;
const LEFT_X = MARGIN;
const RIGHT_X = MARGIN + 108;
const WIDTH = RIGHT_X + BUTTON_W + MARGIN;
const HEIGHT = BODY_SIZE;
const NUMBER_BOX = { x: 27, y: 43 };
const SLOT = { w: 24, h: 27, spacing: 25 };
const DIGIT_FONT_SCALE = 2;
const DIGIT_ADVANCE = 30;
const DIGIT_COLOR = '#ece8ff';
const ROLL_MS = 120;

// Nub motion (counter_input.gd): in, deeper on hold, out past rest, home.
const PRESS_PX = 3;
const HOLD_EXTRA_PX = 4;
const OVERSHOOT_PX = 2;
const PRESS_MS = 45;
const DEEPEN_MS = 80;
const RELEASE_MS = 160;

// Hold-to-repeat, the game's ramp.
const TAP_MS = 200;
const REPEAT_START_MS = 130;
const REPEAT_MIN_MS = 55;
const REPEAT_RAMP_MS = 900;

// The bump at either end: the body knocks side to side.
const BUMP_PX = 1;
const BUMP_CYCLES = 4;
const BUMP_STEP_MS = 40;

const STEP_BUZZ_MS = 6;
const BUMP_BUZZ_MS = 24;
// The left share of the counter is the left nub, which takes one back. A press
// anywhere else counts one, so a thumb on the digits counts too.
const NUB_REACH = 0.4;

type Side = -1 | 1;

interface Drum {
  from: number;
  to: number;
  dir: number;
  start: number;
}

interface Nub {
  // Offset into the body, in sprite pixels; negative is out past rest.
  at: number;
  fromAt: number;
  toAt: number;
  start: number;
  ms: number;
  // A release runs out to the overshoot first, then home.
  thenHome: boolean;
}

export interface Counter {
  element: HTMLCanvasElement;
  value: () => number;
  type: (digit: number) => void;
  backspace: () => void;
  clear: () => void;
  setLocked: (locked: boolean) => void;
  onChange: (listener: (value: number) => void) => void;
}

const easeOut = (t: number): number => 1 - (1 - t) ** 3;

export function makeCounter(start = 0): Counter {
  const canvas = document.createElement('canvas');
  canvas.className = 'counter';
  canvas.setAttribute('role', 'spinbutton');
  canvas.setAttribute('aria-valuemin', '0');
  canvas.setAttribute('aria-valuemax', String(COUNTER_MAX));
  canvas.tabIndex = 0;
  const context = spriteCanvas(canvas, WIDTH, HEIGHT, ART_SCALE);
  let value = start;
  let locked = false;
  let listeners: ((value: number) => void)[] = [];
  const drums: Drum[] = digitsOf(start).map((d) => ({ from: d, to: d, dir: 0, start: 0 }));
  const leftNub = restingNub();
  const rightNub = restingNub();
  const nubOf = (side: Side): Nub => (side < 0 ? leftNub : rightNub);
  let bumpStart = -1;
  let held: { side: Side; pointer: number; since: number; next: number; deep: boolean } | null =
    null;
  let frame = 0;
  let body: HTMLImageElement | null = null;
  let nubArt: HTMLImageElement | null = null;
  let font: Font | null = null;

  void Promise.all([
    loadSprite('/ui/counter-body.png'),
    loadSprite('/ui/counter-button.png'),
    loadFont('virtue'),
  ]).then(([b, n, f]) => {
    body = b;
    nubArt = n;
    font = f;
    animate();
  });

  function setValue(next: number): void {
    if (next === value) return;
    const dir = next > value ? 1 : -1;
    const now = performance.now();
    const after = digitsOf(next);
    drums.forEach((drum, i) => {
      const digit = after[i] as number;
      if (digit === drum.to) return;
      drum.from = drum.to;
      drum.to = digit;
      drum.dir = dir;
      drum.start = prefersLessMotion() ? now - ROLL_MS : now;
    });
    value = next;
    canvas.setAttribute('aria-valuenow', String(value));
    for (const listener of listeners) listener(value);
    animate();
  }

  function step(side: Side): void {
    const next = value + side;
    if (next < 0 || next > COUNTER_MAX) {
      bump();
      return;
    }
    setValue(next);
    play('tick');
    buzz(STEP_BUZZ_MS);
  }

  function bump(): void {
    bumpStart = performance.now();
    play('bump');
    buzz(BUMP_BUZZ_MS);
    animate();
  }

  function moveNub(side: Side, to: number, ms: number, thenHome = false): void {
    const nub = nubOf(side);
    nub.fromAt = nub.at;
    nub.toAt = to;
    nub.start = performance.now();
    nub.ms = ms;
    nub.thenHome = thenHome;
    animate();
  }

  function press(side: Side, pointer: number): void {
    if (locked) return;
    const now = performance.now();
    held = { side, pointer, since: now, next: now + TAP_MS, deep: false };
    moveNub(side, PRESS_PX, PRESS_MS);
    play('click');
    step(side);
  }

  function release(pointer: number): void {
    if (!held || held.pointer !== pointer) return;
    const side = held.side;
    held = null;
    moveNub(side, -OVERSHOOT_PX, RELEASE_MS / 3, true);
  }

  // The hold repeats, faster the longer it is held, as the game's does.
  function repeatHeld(now: number): void {
    if (!held || now < held.next) return;
    if (!held.deep) {
      held.deep = true;
      moveNub(held.side, PRESS_PX + HOLD_EXTRA_PX, DEEPEN_MS);
    }
    step(held.side);
    const ramp = Math.min(1, (now - held.since - TAP_MS) / REPEAT_RAMP_MS);
    held.next = now + REPEAT_START_MS + (REPEAT_MIN_MS - REPEAT_START_MS) * ramp;
  }

  function tickNub(nub: Nub, now: number): boolean {
    const t = nub.ms <= 0 ? 1 : Math.min(1, (now - nub.start) / nub.ms);
    nub.at = nub.fromAt + (nub.toAt - nub.fromAt) * easeOut(t);
    if (t < 1) return true;
    if (!nub.thenHome) return false;
    nub.thenHome = false;
    nub.fromAt = nub.at;
    nub.toAt = locked ? PRESS_PX : 0;
    nub.start = now;
    nub.ms = (RELEASE_MS * 2) / 3;
    return true;
  }

  function draw(now: number): boolean {
    context.clearRect(0, 0, WIDTH, HEIGHT);
    if (!body || !nubArt || !font) return true;
    let moving = held !== null;
    for (const side of [-1, 1] as Side[]) moving = tickNub(nubOf(side), now) || moving;
    const shake = bumpOffset(now);
    moving = moving || shake !== null;
    const dx = shake ?? 0;
    drawNub(context, nubArt, LEFT_X + Math.round(leftNub.at), false);
    drawNub(context, nubArt, RIGHT_X - Math.round(rightNub.at), true);
    context.drawImage(body, BODY_X + dx, 0);
    for (let i = 0; i < DIGITS; i++) {
      moving =
        drawDrum(
          context,
          font,
          drums[i] as Drum,
          BODY_X + dx + NUMBER_BOX.x + i * SLOT.spacing,
          now,
        ) || moving;
    }
    return moving;
  }

  function bumpOffset(now: number): number | null {
    if (bumpStart < 0) return null;
    const beat = Math.floor((now - bumpStart) / BUMP_STEP_MS);
    if (beat >= BUMP_CYCLES || prefersLessMotion()) {
      bumpStart = -1;
      return null;
    }
    return beat % 2 === 0 ? BUMP_PX : -BUMP_PX;
  }

  function animate(): void {
    if (frame) return;
    frame = requestAnimationFrame((now) => {
      frame = 0;
      repeatHeld(now);
      if (draw(now)) animate();
    });
  }

  function sideAt(event: PointerEvent): Side {
    const box = canvas.getBoundingClientRect();
    const across = (event.clientX - box.left) / box.width;
    return across < NUB_REACH ? -1 : 1;
  }

  canvas.addEventListener('pointerdown', (event) => {
    const side = sideAt(event);
    if (held) return;
    event.preventDefault();
    canvas.setPointerCapture(event.pointerId);
    press(side, event.pointerId);
  });
  for (const end of ['pointerup', 'pointercancel', 'lostpointercapture'] as const) {
    canvas.addEventListener(end, (event) => release(event.pointerId));
  }
  canvas.addEventListener('contextmenu', (event) => event.preventDefault());
  canvas.addEventListener('keydown', (event) => {
    const side: Side | null =
      event.key === 'ArrowUp' || event.key === 'ArrowRight' || event.key === '+'
        ? 1
        : event.key === 'ArrowDown' || event.key === 'ArrowLeft' || event.key === '-'
          ? -1
          : null;
    if (side === null || locked) return;
    event.preventDefault();
    moveNub(side, -OVERSHOOT_PX, PRESS_MS, true);
    play('click');
    step(side);
  });

  return {
    element: canvas,
    value: () => value,
    type(digit) {
      if (locked) return;
      const next = value * 10 + digit;
      if (next > COUNTER_MAX) {
        bump();
        return;
      }
      setValue(next);
      play('tick');
    },
    backspace() {
      if (locked) return;
      if (value === 0) {
        bump();
        return;
      }
      setValue(Math.floor(value / 10));
      play('tick', 0.85);
    },
    clear() {
      if (locked || value === 0) return;
      setValue(0);
      play('tick', 0.7);
    },
    setLocked(on) {
      if (on === locked) return;
      locked = on;
      held = null;
      moveNub(-1, on ? PRESS_PX : 0, RELEASE_MS);
      moveNub(1, on ? PRESS_PX : 0, RELEASE_MS);
    },
    onChange(listener) {
      listeners = [...listeners, listener];
    },
  };
}

const restingNub = (): Nub => ({ at: 0, fromAt: 0, toAt: 0, start: 0, ms: 0, thenHome: false });

const digitsOf = (value: number): number[] =>
  String(value).padStart(DIGITS, '0').split('').map(Number);

function drawNub(
  context: CanvasRenderingContext2D,
  art: HTMLImageElement,
  x: number,
  flipped: boolean,
): void {
  if (!flipped) {
    context.drawImage(art, x, 0);
    return;
  }
  context.save();
  context.translate(x + BUTTON_W, 0);
  context.scale(-1, 1);
  context.drawImage(art, 0, 0);
  context.restore();
}

// One drum, clipped to its slot: the old digit rolls off as the new one rolls
// in, up for a count up and down for a count down. True while it rolls.
function drawDrum(
  context: CanvasRenderingContext2D,
  font: Font,
  drum: Drum,
  x: number,
  now: number,
): boolean {
  const t = Math.min(1, (now - drum.start) / ROLL_MS);
  const rolling = drum.from !== drum.to;
  const y = NUMBER_BOX.y;
  context.save();
  context.beginPath();
  context.rect(x, y, SLOT.w, SLOT.h);
  context.clip();
  // A drum at rest shows its digit square in the slot.
  const shift = rolling ? Math.round(-drum.dir * easeOut(t) * DIGIT_ADVANCE) : 0;
  drawDigit(context, font, drum.from, x, y + shift);
  if (rolling) drawDigit(context, font, drum.to, x, y + shift + drum.dir * DIGIT_ADVANCE);
  context.restore();
  if (t >= 1) drum.from = drum.to;
  return rolling && t < 1;
}

function drawDigit(
  context: CanvasRenderingContext2D,
  font: Font,
  digit: number,
  x: number,
  y: number,
): void {
  const glyph = font.data.chars[String(48 + digit)];
  if (!glyph) return;
  const [gx, gy, w, h, xOffset, yOffset, advance] = glyph;
  const ink = (font.bottom - font.top) * DIGIT_FONT_SCALE;
  const left =
    x + Math.round((SLOT.w - advance * DIGIT_FONT_SCALE) / 2) + xOffset * DIGIT_FONT_SCALE;
  const top = y + Math.round((SLOT.h - ink) / 2) + (yOffset - font.top) * DIGIT_FONT_SCALE;
  context.drawImage(
    tinted(font),
    gx,
    gy,
    w,
    h,
    left,
    top,
    w * DIGIT_FONT_SCALE,
    h * DIGIT_FONT_SCALE,
  );
}

let digitAtlas: HTMLCanvasElement | null = null;

// The digit colour, baked into one copy of the atlas.
function tinted(font: Font): HTMLCanvasElement {
  if (digitAtlas) return digitAtlas;
  const canvas = document.createElement('canvas');
  canvas.width = font.image.width;
  canvas.height = font.image.height;
  const context = canvas.getContext('2d') as CanvasRenderingContext2D;
  context.drawImage(font.image, 0, 0);
  context.globalCompositeOperation = 'source-in';
  context.fillStyle = DIGIT_COLOR;
  context.fillRect(0, 0, canvas.width, canvas.height);
  digitAtlas = canvas;
  return canvas;
}
